<template>
  <tr
    @mouseover="$emit('changeHoveredId', props._key)"
    @mouseout="$emit('changeHoveredId', '')"
  >
    <td class="sc-data-list-select">
      <v-list-item class="ml-0 pl-0 pr-0">
        <template v-if="allowSelect" #prepend>
          <v-checkbox
            hide-details
            class="ml-0 mr-4"
            :model-value="selected"
            @update:model-value="$emit('toggleItem', props)"
          />
        </template>
        <CommonChip
          v-if="!allowCustomSort"
          class="sc-nbr sc-chip--dark"
          :class="getNBRPropertyTypeClass(props.type)"
        >
          <span>{{ props.number }}</span>
        </CommonChip>
      </v-list-item>
    </td>
    <td v-if="allowCustomSort" class="sc-data-list-select pl-0">
      <div class="d-flex align-center gap-2 mt-1">
        <v-btn
          icon
          :loading="isCustomOrderLoading"
          data-track="availability.tour.move-down"
          @click="$emit('update:tour-sort', { key: props._key, order: props.number + 1 })"
        >
          <v-icon>mdi-chevron-down</v-icon>
        </v-btn>
        <CommonChip
          class="sc-nbr sc-chip--dark"
          :class="getNBRPropertyTypeClass(props.type)"
        >
          <span>{{ props.number }}</span>
        </CommonChip>
        <v-btn
          icon
          :loading="isCustomOrderLoading"
          data-track="availability.tour.move-up"
          @click="$emit('update:tour-sort', { key: props._key, order: props.number - 1 })"
        >
          <v-icon>mdi-chevron-up</v-icon>
        </v-btn>
      </div>
    </td>
    <td v-if="allowPhoto" class="sc-data-list-property-photo">
      <RecordLink
        :disabled="!allowLink"
        :to="getAccountRoute({
          name: 'availability',
          params: { id: props._key },
          activeAccountId: activeAccountId
        })"
        class="property-name"
        :class="{ 'cursor-default': !allowLink }"
      >
        <Photo
          :photo-url="props.photo ? props.photo.url : ''"
          :transaction-type-label="props.status_label"
          class="availability-list-photo"
          photo-class="rounded"
        />
      </RecordLink>
    </td>
    <td class="sc-data-list-property-address property-address">
      <div v-if="!props.active">
        <v-chip
          size="x-small"
          color="black"
          class="mb-1 text-white"
        >
          Inactive
        </v-chip>
      </div>
      <div class="record-id">Availability ID# {{ props._key }}</div>
      <RecordLink
        :disabled="!allowLink"
        :to="getAccountRoute({
          name: 'availability',
          params: { id: props._key },
          activeAccountId: activeAccountId
        })"
        class="property-name"
        :class="{ 'cursor-default': !allowLink }"
      >
        <v-tooltip v-if="getComments(props).length" location="end">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              style="float: right"
              icon
              v-bind="tooltipProps"
            >
              <v-icon size="20">mdi-comment-text</v-icon>
            </v-btn>
          </template>
          <div style="max-width: 400px">
            {{ getComments(props) }}
          </div>
        </v-tooltip>
        <div>
          <span v-if="props.property.name">{{ props.property.name }}</span>
          <span v-else class="na">n/a</span>
        </div>
        <div style="font-weight: normal">
          <span v-if="props.address.street">{{ props.address.street }}
            <span v-if="props.suite">, Suite {{ props.suite }}</span>
          </span>
          <span v-else class="na">n/a</span>
        </div>
        <div style="font-weight: normal">
          <span v-if="props.property.address.city || props.property.address.state || props.property.address.postal">
            {{ props.property.address.city }}, {{ props.property.address.state }}
            {{ props.property.address.postal }}
            <span v-if="props.property.address.country !== 'US'">{{ props.property.address.country }}</span>
          </span>
          <span v-else class="na">n/a</span>
        </div>
      </RecordLink>
      <div class="mt-2">
        <AvailabilityChipsWrapper
          allow-wrap
          :agency-type="props.agency_type"
          :in-house="props.in_house"
          :status="props.status"
          :type="props.type"
          :mixed-use="props.mixed_use"
          :vacancy-type="props.vacancy_type"
          :is-portfolio="props.is_portfolio"
          :lease-pending="props.lease_pending"
          :contract-pending="props.contract_pending"
          :is-published-to-web="props.published_to_web.value"
          :transaction-status="props.status_label"
          :show-transaction-status="!allowPhoto"
        />
      </div>
    </td>
    <td :class="`sc-data-list-property-data ${!isLand(props) ? 'available-space' : '' }`">
      <div>
        <BuildingSizeLabel
          :value="props.available_space"
        />
      </div>
      <div>
        <BuildingSizeLabel
          :value="props.divisible_space"
        />
      </div>
    </td>
    <td class="sc-data-list-property-data">
      <div>
        <BuildingSizeLabel
          :value="props.building_size"
        />
      </div>
      <div>
        <Label
          :value="props.property.construction_status"
        />&nbsp;
        <Label
          :value="props.year_built"
        />
      </div>
      <div class="vacancy">
        <Label
          :value="showVacancyStatus(props) ? props.vacancy_status : null"
        />
      </div>
    </td>
    <td :class="`sc-data-list-property-data ${isLand(props) ? 'available-space' : '' }`">
      <div>
        <template v-if="isValuePresent(props.acres)">
          <LandSizeLabel
            :value="props.acres"
          />
          <POLIcon
            v-if="props.land_pol"
          />
        </template>
        <span v-else class="na">N/A</span>
      </div>
      <div>
        <LandSizeLabel
          :value="props.divisible_land_size"
        />
      </div>
    </td>
    <td class="sc-data-list-property-data sc-data-list-core-data">
      <span v-if="props.bts">BTS</span>
      <BuildingSizeLabel
        v-else-if="props.office_space"
        :value="props.office_space"
      />
      <span v-else class="na">N/A</span>
    </td>
    <td class="sc-data-list-door-data sc-data-list-core-data">
      <div>
        <BuildingDimensionLabels
          :value1="props.details.building.ceiling_height.min"
          :value2="props.details.building.ceiling_height.max"
        />
      </div>
      <div>
        <Label
          :value="props.details.loading_doors.doors.truck_level.total"
        />
      </div>
      <div>
        <Label
          :value="props.details.loading_doors.doors.ground_level.total"
        />
      </div>
    </td>
    <td class="sc-data-list-property-data sc-data-list-core-data">
      <span v-if="props.sprinklers">
        {{ props.sprinklers }}
      </span>
      <span v-else class="na">N/A</span>
    </td>
    <td class="sc-data-list-property-data">
      <template v-if="props.for_sale">
        <div>
          <AvailabilitySalesPriceLabel
            :availability="props"
            :show-psf="false"
            :show-label="false"
          />
        </div>
        <div>
          <AvailabilitySalesPriceLabel
            :availability="props"
            :show-price="false"
            :show-label="false"
            hide-icons
          />
        </div>
        <div>
          <Label
            v-if="props.sale"
            :value="isValuePresent(props.sale.cap_rate) ? $filters.formatPercent(props.sale.cap_rate) : null"
          />
        </div>
      </template>
      <template v-else>
        <Label />
        <br>
        <Label />
        <br>
        <Label />
      </template>
    </td>
    <td class="sc-data-list-property-data">
      <span v-if="props.for_lease && props.lease_rate">
        {{ $filters.formatCurrency(props.lease_rate) }} / {{ getRateEnteredAsLabel(props.lease.rate_entered_as) }}
        <br>
        {{ props.lease_type }}
        <v-tooltip v-if="props.lease_private === true" location="top">
          <template #activator="{ props: tooltipProps }">
            <v-avatar
              color="red"
              size="16"
              v-bind="tooltipProps"
              class="mr-1"
            >
              <span class="text-white text-caption">P</span>
            </v-avatar>
          </template>
          <span>The Lease Rate is Private</span>
        </v-tooltip><v-tooltip v-if="props.lease && props.lease.guidance_price === true" location="top">
          <template #activator="{ props: tooltipProps }">
            <v-avatar
              color="blue"
              size="16"
              v-bind="tooltipProps"
            >
              <span class="text-white text-caption">G</span>
            </v-avatar>
          </template>
          <span>The Lease Rate is a guidance rate</span>
        </v-tooltip></span>
      <span v-else class="na">N/A<br>N/A</span>
    </td>
    <td class="sc-data-list-property-data">
      <div v-if="props.opex">
        <Label
          :value="getAvailabilityExpensesLabel([{opex: props.opex}])"
        />
      </div>
      <div v-else-if="props.tmi">
        <Label
          :value="getAvailabilityExpensesLabel([{tmi: props.tmi}])"
        />
      </div>
      <div v-else>
        <Label
          :value="getAvailabilityExpensesLabel(props.operating_expenses)"
        />
      </div>
      <div>
        <span v-if="props.ti_allowance && !props.ti_negotiable">
          {{ $filters.formatCurrency(props.ti_allowance) }}
        </span>
        <span v-else-if="props.ti_negotiable">Negotiable</span>
        <Label v-else />
      </div>
    </td>
    <td class="sc-data-list-property-data">
      <span v-if="props.date_available">{{ getDateAvailableLabel(props.date_available) }}</span>
      <Label v-else />
      <br>
      <span v-if="props.date_on_market" style="white-space: nowrap">
        {{ $filters.formatDate(props.date_on_market) }}
        <small>({{ monthsOnMarket(props.date_on_market) }} months)</small>
      </span>
      <Label v-else />
    </td>
    <td class="sc-data-list-property-contacts">
      <div v-for="owner in props.true_owner" :key="owner._key">
        <router-link
          v-if="owner.company && owner.company._key"
          :to="getAccountRoute({
            name: 'company',
            params: { id: owner.company._key },
            activeAccountId: activeAccountId
          })"
          class="company-name d-inline"
        >
          {{ owner.company.name }}
        </router-link>
      </div>
    </td>
    <td class="sc-data-list-property-contacts">
      <div v-if="getCompanyName(props.brokers)" class="mb-2">
        <Label
          :value="getCompanyName(props.brokers)"
        />
      </div>
      <div v-if="props.brokers && props.brokers.length">
        <AvailabilityBrokersInfo
          :brokers="props.brokers"
          :active-account-id="activeAccountId"
          with-tooltip-for-all-brokers
        />
      </div>
      <div v-else-if="props.property.brokers && props.property.brokers.length">
        <AvailabilityBrokersInfo
          :brokers="props.property.brokers"
          :active-account-id="activeAccountId"
          with-tooltip-for-all-brokers
        />
      </div>
      <div v-else>
        <span class="na">N/A</span>
      </div>
    </td>
    <td class="sc-data-list-updated-created">
      <div v-if="props.updated" class="updated-date">
        {{ $filters.formatJSDateTime(props.updated.date) }}, {{ props.updated.name }}
      </div>
      <span v-else>
        N/A
      </span>
      <div v-if="props.created" class="created-date">
        {{ $filters.formatJSDateTime(props.created.date) }}, {{ props.created.name }}
      </div>
      <span v-else>
        N/A
      </span>
      <div class="status-cd">
        {{ props.status }}
      </div>
      <AvailabilityVerificationLabel
        v-if="props.verification"
        :verification="props.verification"
      />
      <span v-else class="na">N/A</span>
      <div v-if="props.legacy_id" style="font-size: .8em; margin-top: 5px;">Legacy ID: {{ props.legacy_id }}</div>
      <CustomFieldCell v-if="customField" :title="getCustomFieldValue(props, customField)">
        <CustomFieldValue :value="getCustomFieldValue(props, customField)" />
      </CustomFieldCell>
    </td>
  </tr>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';

import { getDateDiffAbs } from '@/utils/filters';
import { getAccountRouteHandler } from '@/bundles/App/helpers/accountRoute';
import { getNBRPropertyTypeClass } from '@/bundles/Property/helpers/colors/getNBRPropertyTypeClass';
import { roundToDecimal } from '@/bundles/App/helpers/mathHelpers';
import { getAvailabilityExpensesLabel } from '@/bundles/Property/helpers/expenses/getAvailabilityExpensesLabel';
import { getRateEnteredAsLabel } from '@/bundles/Availabilities/helpers/availabilityConsts';
import { isValuePresent } from '@/bundles/App/helpers/isValuePresent';
import { getDateAvailableLabel } from '@/bundles/Availabilities/helpers';
import { ConstructionStatusEnum } from '@/bundles/Property/types';
import { AvailabilityCustomFieldsEnum } from '@/bundles/Availabilities/types';
import { getCustomFieldValue } from '@/bundles/Availabilities/helpers/availabilityCustomFieldMapper';
import { Nullable } from '@/utils/types';

import Photo from '@/components/common-components/photo/Photo.vue';
import BuildingSizeLabel from '@/bundles/MeasurementUnits/components/BuildingSizeLabel.vue';
import LandSizeLabel from '@/bundles/MeasurementUnits/components/LandSizeLabel.vue';
import POLIcon from '@/bundles/Comparables/components/POLIcon.vue';
import AvailabilityVerificationLabel from '@/bundles/Availabilities/components/AvailabilityVerificationLabel.vue';
import AvailabilityChipsWrapper from '@/bundles/Availabilities/components/chips/AvailabilityChipsWrapper.vue';
import AvailabilityBrokersInfo from '@/bundles/Availabilities/components/content-view/common/AvailabilityBrokersInfo.vue';
import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import Label from '@/bundles/Common/components/Label.vue';
import CustomFieldValue from '@/bundles/BaseTable/components/CustomFieldValue.vue';
import CustomFieldCell from '@/bundles/BaseTable/components/CustomFieldCell.vue';
import BuildingDimensionLabels from '@/bundles/MeasurementUnits/components/BuildingDimensionLabels.vue';
import AvailabilitySalesPriceLabel from '@/bundles/Availabilities/components/cards/details/AvailabilitySalesPriceLabel.vue';
import RecordLink from '@/bundles/Common/components/links/RecordLink.vue';

export default defineComponent({
  name: 'ClientContent',

  components: { RecordLink,
    AvailabilitySalesPriceLabel,
    CommonChip,
    AvailabilityChipsWrapper,
    Photo,
    BuildingSizeLabel,
    LandSizeLabel,
    POLIcon,
    AvailabilityVerificationLabel,
    BuildingDimensionLabels,
    Label,
    CustomFieldCell,
    CustomFieldValue,
    AvailabilityBrokersInfo
  },

  props: {
    props: {
      type: Object,
      required: true,
    },
    selected: Boolean,
    displayBrokers: Boolean,
    displayOwners: Boolean,
    trackTour: Boolean,
    allowSelect: {
      type: Boolean,
      default: true
    },
    activeAccountId: {
      type: Number,
      required: true
    },
    allowLink: {
      type: Boolean,
      default: true
    },
    allowPhoto: {
      type: Boolean,
      default: true
    },
    allowCustomSort: Boolean,
    isCustomOrderLoading: Boolean,
    withTooltipForAllBrokers: Boolean,
    customField: {
      type: String as PropType<Nullable<AvailabilityCustomFieldsEnum>>,
      default: null,
    },
  },

  emits: ['changeHoveredId', 'toggleItem', 'update:tour-sort'],

  methods: {
    getCustomFieldValue,
    getDateAvailableLabel,
    isValuePresent,
    getNBRPropertyTypeClass,
    getAvailabilityExpensesLabel,
    getRateEnteredAsLabel,
    isLand (props) {
      return props.type?.toLowerCase() === 'land';
    },
    showVacancyStatus (props) {
      return props.property.construction_status === ConstructionStatusEnum.existing;
    },
    getComments (props) {
      const listingComments = props.listing_comments || '';
      const propertyComments = props.property_comments || '';

      if (listingComments && propertyComments) {
        return listingComments + ' -- ' + propertyComments;
      }

      if (listingComments && !propertyComments) {
        return listingComments;
      }

      return propertyComments;
    },
    monthsOnMarket (date) {
      const days = getDateDiffAbs(date, ['days']).days;

      return days < 30 ? roundToDecimal(days / 30, 1) : Math.round(days / 30);
    },
    getAccountRoute ({ name, params, activeAccountId }: {
      name: string;
      params: { id: number };
      activeAccountId: number;
    }) {
      return getAccountRouteHandler({
        name,
        params,
        activeAccountId
      });
    },
    getCompanyName (availabilityBrokers) {
      return availabilityBrokers?.[0]?.company?.name || '';
    },
  }
});
</script>

<style scoped>
.property-name {
  color: rgba(var(--v-theme-on-surface), var(--v-sc-ink-opacity));
  font-weight: bold;
  display: block;
  text-decoration: none;
}
/* client table styles */
td.available-space {
  font-weight: 700;
}
.availability-list-photo {
  width: 150px;
  max-width: 100%;
}
</style>
